import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import resolveExcelEditConfig from "../../../../Controls/ExcelWebEdit/excelEditConfig";
import { toColumnLabel } from "../utils/columnUtils";
import evaluateFormula, { isFormula, getFormulaRefs } from "../utils/formulaEngine";
import { patchWorkbookCells } from "../utils/xlsxPatcher";
import { formatExcelDate, parseDateInput } from "../utils/dateFormat";

/**
 * useCellEditing — in-place cell editing for SimpleExcelViewer.
 *
 * Opt-in per reader email via AppContext.ExcelWebEdit (same config the
 * standalone editor used). Edits are committed from the formula bar,
 * applied to the display model immediately, and saved to the server by
 * patching ONLY the changed cells' XML inside the original .xlsx (via
 * xlsxPatcher/JSZip) so charts, drawings, images, styles and every other
 * part survive byte-for-byte, then POSTing the result to the save endpoint.
 *
 * Editing is blocked while a sort or filter is active: those reorder the
 * DISPLAY rows, so a display-coordinate edit could land on the wrong
 * file row.
 */
export default function useCellEditing({
  userId, documentId, openId, fileName, fileType,
  activeSheet, sheetDataByName, setSheetDataByName, workbookBufferRef,
  sortActive, filterEnabled,
  applySheetRename, // (oldName, newName) => void — used to revert renames on cancel
  onWorkbookSaved,  // (base64Xlsx) => void — lets the page cache the SAVED file
}) {
  const config = useMemo(() => resolveExcelEditConfig(userId), [userId]);

  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  // True right after a successful save - the viewer shows a success popup.
  const [saveSuccess, setSaveSuccess] = useState(false);
  // { [sheetName]: { "row:col": newValue } } — display coords, 0-based.
  const editedCellsRef = useRef({});
  // User-entered formula cells per sheet: { [sheet]: Set("row:col") }.
  // Recomputed live on every edit so dependents update like Excel.
  const formulaCellsRef = useRef({});
  // Recalculated results of the FILE's own formula cells (dependents of an
  // edit): { [sheet]: { "row:col": value } }. Written into the cells' cached
  // <v> on save so the saved workbook re-opens with the new results.
  const recalcRef = useRef({});
  const onWorkbookSavedRef = useRef(onWorkbookSaved);
  onWorkbookSavedRef.current = onWorkbookSaved;
  // Undo stack: one entry per cell-edit "burst" (live keystrokes into the
  // same cell coalesce into a single entry, like Excel's undo). Entries
  // carry a batch id so one Ctrl+Z reverts a whole multi-cell paste.
  const undoStackRef = useRef([]);
  const undoBatchRef = useRef(0);
  // Guard so an undo application doesn't push onto the undo stack itself.
  const isUndoingRef = useRef(false);
  // Pending sheet renames: { [currentName]: originalName } (unsaved).
  const renameOriginalsRef = useRef({});
  // Keep the latest rename applier in a ref (stable effect deps).
  const applySheetRenameRef = useRef(applySheetRename);
  applySheetRenameRef.current = applySheetRename;

  // Model snapshot (rows/richHtml/formulas per sheet) taken before the first
  // unsaved edit — Cancel restores it. Cleared on save / cancel.
  const sessionSnapshotRef = useRef(null);

  const hasPendingRenames = useCallback(() =>
    Object.keys(renameOriginalsRef.current).some(
      (cur) => renameOriginalsRef.current[cur] !== cur
    ), []);

  // The patcher works on OOXML (.xlsx/.xlsm) only; legacy binary .xls stays
  // view-only.
  const ext = String(fileType || "").toLowerCase();
  const formatEditable = ext !== "xls";
  const canEdit = !!(config && config.canEdit === true) && formatEditable;
  const canSave = canEdit && !!(config && config.canSave === true);
  const editBlocked = !!(sortActive || filterEnabled);

  const toggleEditMode = useCallback(() => setEditMode((m) => !m), []);

  // Cancel editing: revert every UNSAVED edit back to its original value
  // (edits already saved to the server are untouched) and leave edit mode.
  const cancelEdits = useCallback(() => {
    // Restore every sheet a recalculation touched from the snapshot taken
    // when the first unsaved edit was made (dependent formula results on any
    // sheet revert along with the typed cells).
    const snap = sessionSnapshotRef.current;
    if (snap) {
      setSheetDataByName((prev) => {
        const out = { ...prev };
        Object.keys(snap).forEach((sheetName) => {
          if (!prev[sheetName]) return;
          out[sheetName] = {
            ...prev[sheetName],
            rows: snap[sheetName].rows,
            richHtml: snap[sheetName].richHtml,
            formulas: snap[sheetName].formulas,
            styles: snap[sheetName].styles,
            dateFormats: snap[sheetName].dateFormats,
            numFmts: snap[sheetName].numFmts,
          };
        });
        return out;
      });
    }
    sessionSnapshotRef.current = null;
    recalcRef.current = {};
    if (!snap) Object.entries(editedCellsRef.current).forEach(([sheetName, cells]) => {
      setSheetDataByName((prev) => {
        const model = prev[sheetName];
        if (!model) return prev;
        const rows = (model.rows || []).slice();
        Object.entries(cells).forEach(([key, entry]) => {
          const [r, c] = key.split(":").map(Number);
          if (!rows[r]) return;
          rows[r] = rows[r].slice();
          const s = entry.oldValue;
          const num = Number(s);
          rows[r][c] = s !== "" && String(s).trim() !== "" && !isNaN(num) && isFinite(num) ? num : s;
        });
        return { ...prev, [sheetName]: { ...model, rows } };
      });
    });
    // Revert any unsaved sheet renames back to their original names.
    Object.entries(renameOriginalsRef.current).forEach(([current, original]) => {
      if (current !== original && typeof applySheetRenameRef.current === "function") {
        applySheetRenameRef.current(current, original);
      }
    });
    renameOriginalsRef.current = {};
    editedCellsRef.current = {};
    formulaCellsRef.current = {};
    undoStackRef.current = [];
    setDirty(false);
    setStatusMsg("");
    setEditMode(false);
  }, [setSheetDataByName]);

  // Batch commit: apply several cell edits against ONE working copy of the
  // sheet (multi-cell paste needs this — repeated single commits within the
  // same tick would each start from the stale model and clobber each other).
  const commitCells = useCallback((cellEdits) => {
    if (!canEdit || !activeSheet || !Array.isArray(cellEdits) || cellEdits.length === 0) return;
    const model = sheetDataByName[activeSheet];
    if (!model) return;
    if (!sessionSnapshotRef.current) {
      const snap = {};
      Object.keys(sheetDataByName).forEach((sn) => {
        const m = sheetDataByName[sn];
        if (m) {
          snap[sn] = {
            rows: m.rows, richHtml: m.richHtml, formulas: m.formulas,
            styles: m.styles, dateFormats: m.dateFormats, numFmts: m.numFmts,
          };
        }
      });
      sessionSnapshotRef.current = snap;
    }

    const fset = formulaCellsRef.current[activeSheet];
    const anyWorkbookFormulas = Object.values(sheetDataByName).some(
      (m) => m && m.formulas && Object.keys(m.formulas).length > 0
    );
    // Clone every row whenever ANY formula may recompute (user or file) or
    // when the batch touches multiple rows. Otherwise clone only the touched
    // row — the fast path that keeps the grid's per-row memoization intact.
    const deepClone =
      cellEdits.length > 1 ||
      cellEdits.some((e2) => isFormula(String(e2.text))) ||
      (fset && fset.size > 0) ||
      anyWorkbookFormulas;
    const rows = deepClone
      ? (model.rows || []).map((r) => (r ? r.slice() : []))
      : (model.rows || []).slice();

    const richHtml = { ...(model.richHtml || {}) };
    const formulas = { ...(model.formulas || {}) };
    // Cell formatting + date formats travel with pasted cells. Cloned only
    // when this commit actually changes formatting (keeps memoized styles).
    const touchesFormat = cellEdits.some((e2) => e2.style !== undefined);
    const styles = touchesFormat ? { ...(model.styles || {}) } : model.styles;
    const dateFormats = touchesFormat ? { ...(model.dateFormats || {}) } : (model.dateFormats || {});
    const numFmts = touchesFormat ? { ...(model.numFmts || {}) } : (model.numFmts || {});

    // Working copies of every sheet touched by this commit (the active sheet
    // always; other sheets only once a cross-sheet formula there changes).
    const work = { [activeSheet]: { rows, richHtml, formulas, changed: true } };
    const workFor = (sheetName) => {
      if (work[sheetName]) return work[sheetName];
      const m = sheetDataByName[sheetName];
      if (!m) return null;
      work[sheetName] = {
        rows: (m.rows || []).map((r) => (r ? r.slice() : [])),
        richHtml: { ...(m.richHtml || {}) },
        formulas: { ...(m.formulas || {}) },
        changed: false,
      };
      return work[sheetName];
    };
    // Resolver used by the formula engine: reads the WORKING rows so
    // dependent formulas see freshly-typed values — also across sheets
    // (Sheet2!A1 / 'My Sheet'!A1:B5).
    const makeGetCell = (homeSheet) => (r, c, sheetName) => {
      const target = sheetName || homeSheet;
      const src = work[target]
        ? work[target].rows
        : (sheetDataByName[target] ? sheetDataByName[target].rows : null);
      const v = src && src[r] ? src[r][c] : "";
      return v == null ? "" : v;
    };
    const getCell = makeGetCell(activeSheet);

    let anyChange = false;
    const undoBatch = ++undoBatchRef.current;
    // Cells whose value changed in this commit: [{ sheet, r, c }]. Seeds the
    // dependency-driven recalculation below.
    const changedCells = [];

    cellEdits.forEach(({ row, col, text, style, richHtml: pastedRich, dateFormat, numFmt, styleSource }) => {
      if (row == null || col == null) return;
      const key = `${row}:${col}`;
      const entered = String(text);
      const isF = isFormula(entered);
      const formatChange = style !== undefined;
      const prevStyle = styles ? styles[key] : undefined;
      const prevDateFormat = dateFormats[key] || null;
      const prevNumFmt = numFmts[key] || null;
      const prevRich = model.richHtml ? model.richHtml[key] : undefined;
      if (formatChange) {
        if (style) styles[key] = { ...style }; else delete styles[key];
        if (dateFormat) dateFormats[key] = dateFormat; else delete dateFormats[key];
        if (numFmt) numFmts[key] = numFmt; else delete numFmts[key];
      }
      // Date-formatted cell: a typed/pasted date is shown in the cell's own
      // format and saved as a real Excel date serial.
      const cellDateFmt = dateFormats[key] || null;
      let fileValue;
      while (rows.length <= row) rows.push([]);
      if (!deepClone) rows[row] = (rows[row] || []).slice();
      if (!rows[row]) rows[row] = [];

      const oldVal = rows[row][col];
      const prevFormula = formulas[key] || null;

      let storedValue;
      if (isF) {
        formulas[key] = entered.trim();
        if (!formulaCellsRef.current[activeSheet]) formulaCellsRef.current[activeSheet] = new Set();
        formulaCellsRef.current[activeSheet].add(key);
        storedValue = evaluateFormula(entered, getCell);
      } else {
        delete formulas[key];
        if (fset) fset.delete(key);
        const num = Number(entered);
        storedValue =
          entered !== "" && entered.trim() !== "" && !isNaN(num) && isFinite(num) ? num : entered;
        if (cellDateFmt && entered.trim() !== "") {
          const serial = typeof storedValue === "number" ? storedValue : parseDateInput(entered, cellDateFmt);
          if (serial != null) {
            storedValue = formatExcelDate(cellDateFmt, serial);
            fileValue = serial;
          }
        }
        // Nothing changed on a plain value edit -> skip (formulas always
        // proceed, and so do pastes that change the cell's formatting).
        if (!formatChange && String(oldVal == null ? "" : oldVal) === String(storedValue)) return;
      }

      // Undo bookkeeping: one entry per cell-edit burst — consecutive live
      // keystrokes into the same cell keep the FIRST entry (whose prev value
      // predates the burst), exactly like Excel's whole-edit undo.
      if (!isUndoingRef.current) {
        const stack = undoStackRef.current;
        const top = stack[stack.length - 1];
        if (!(top && top.sheet === activeSheet && top.row === row && top.col === col)) {
          stack.push({
            sheet: activeSheet, row, col,
            prevValue: oldVal == null ? "" : oldVal,
            prevFormula,
            batch: undoBatch,
            // Formatting before this edit (restored by undo).
            formatChange,
            prevStyle: prevStyle || null,
            prevDateFormat,
            prevNumFmt,
            prevRich: prevRich || null,
            prevStyleSource: editedCellsRef.current[activeSheet] && editedCellsRef.current[activeSheet][key]
              ? editedCellsRef.current[activeSheet][key].styleSource : undefined,
          });
          if (stack.length > 200) stack.shift();
        }
      }

      rows[row][col] = storedValue;
      delete richHtml[key];
      // Pasted rich text keeps its per-run fonts when the text is unchanged.
      if (formatChange && pastedRich && String(storedValue) === entered) richHtml[key] = pastedRich;
      anyChange = true;
      changedCells.push({ sheet: activeSheet, r: row, c: col });
      // A typed value replaces a file formula: its recalculated cache entry
      // (if any) no longer applies.
      const rs = recalcRef.current[activeSheet];
      if (rs) delete rs[key];

      const bySheet =
        editedCellsRef.current[activeSheet] || (editedCellsRef.current[activeSheet] = {});
      const prevEntry = bySheet[key];
      const trueOld = prevEntry ? prevEntry.oldValue : String(oldVal == null ? "" : oldVal);
      // If a plain-value edit lands back on the cell's ORIGINAL value (e.g.
      // Escape reverts live edits, or the user retypes the original), drop the
      // entry entirely so it doesn't count as an unsaved change or log a
      // no-op in history.
      // Style source for the saver: the cell whose file style (xf) the pasted
      // cell takes. Undefined = keep this cell's own file style.
      const styleSrc = formatChange
        ? (styleSource || null)
        : (prevEntry ? prevEntry.styleSource : undefined);
      if (!isF && !styleSrc && String(storedValue) === String(trueOld)) {
        delete bySheet[key];
      } else {
        bySheet[key] = {
          value: storedValue,
          ...(fileValue !== undefined ? { fileValue } : null),
          formula: isF ? entered.trim() : null,
          oldValue: trueOld,
          ...(styleSrc !== undefined ? { styleSource: styleSrc } : null),
        };
      }
    });

    if (!anyChange) return;

    // ---- Dependency-driven recalculation (whole workbook) ----
    // Re-evaluate every formula (user-entered AND the file's own) that reads
    // a changed cell — on ANY sheet — and cascade until nothing else changes,
    // so dependent cells and the charts plotting them update live like Excel
    // (QA: "modified data is not updating the graphs"). Formulas the engine
    // can't evaluate (unsupported functions, external refs) keep their cached
    // Excel result instead of showing an error.
    const depIndex = []; // [{ sheet, key, formula, refs }]
    Object.keys(sheetDataByName).forEach((sheetName) => {
      const f = sheetName === activeSheet ? formulas : (sheetDataByName[sheetName]?.formulas || {});
      Object.keys(f).forEach((fk) => {
        depIndex.push({ sheet: sheetName, key: fk, formula: f[fk], refs: getFormulaRefs(f[fk]) });
      });
    });
    const readsChanged = (entry, changed) => {
      if (entry.refs === null) return true; // unparseable: be safe, recompute
      return entry.refs.some((rg) => {
        const target = rg.sheet || entry.sheet;
        return changed.some((ch) =>
          ch.sheet === target && ch.r >= rg.r1 && ch.r <= rg.r2 && ch.c >= rg.c1 && ch.c <= rg.c2);
      });
    };

    let frontier = changedCells;
    for (let pass = 0; pass < 50 && frontier.length > 0; pass += 1) {
      const next = [];
      const changedNow = frontier; // block-scoped snapshot for the callback
      depIndex.forEach((entry) => {
        if (!readsChanged(entry, changedNow)) return;
        const w = workFor(entry.sheet);
        if (!w) return;
        const [fr, fc] = entry.key.split(":").map(Number);
        const isUserFormula = !!(formulaCellsRef.current[entry.sheet] &&
          formulaCellsRef.current[entry.sheet].has(entry.key));
        const res = evaluateFormula(entry.formula, makeGetCell(entry.sheet));
        if (!isUserFormula && typeof res === "string" && res[0] === "#") return; // keep cached
        if (!w.rows[fr]) w.rows[fr] = [];
        if (String(w.rows[fr][fc] == null ? "" : w.rows[fr][fc]) === String(res)) return;
        w.rows[fr][fc] = res;
        delete w.richHtml[entry.key]; // cached rich text would mask the new value
        w.changed = true;
        next.push({ sheet: entry.sheet, r: fr, c: fc });
        if (isUserFormula) {
          const eb = editedCellsRef.current[entry.sheet];
          if (eb && eb[entry.key]) eb[entry.key].value = res;
        } else {
          // Remember the new result so Save refreshes the file's cached value
          // (otherwise the saved workbook re-opens showing the OLD result).
          if (!recalcRef.current[entry.sheet]) recalcRef.current[entry.sheet] = {};
          recalcRef.current[entry.sheet][entry.key] = res;
        }
      });
      frontier = next;
    }

    setSheetDataByName((prev) => {
      const out = { ...prev };
      Object.keys(work).forEach((sheetName) => {
        const w = work[sheetName];
        if (!w.changed) return;
        out[sheetName] = {
          ...(prev[sheetName] || sheetDataByName[sheetName]),
          rows: w.rows, richHtml: w.richHtml, formulas: w.formulas,
          ...(sheetName === activeSheet && touchesFormat ? { styles, dateFormats, numFmts } : null),
        };
      });
      return out;
    });
    // Dirty only while at least one edit (or sheet rename) remains.
    const anyEdits = Object.values(editedCellsRef.current).some(
      (s) => s && Object.keys(s).length > 0
    );
    setDirty(anyEdits || hasPendingRenames());
  }, [canEdit, activeSheet, sheetDataByName, setSheetDataByName, hasPendingRenames]);

  const commitCell = useCallback(
    (row, col, text) => commitCells([{ row, col, text }]),
    [commitCells]
  );

  // Undo (Ctrl+Z): revert the most recent cell-edit burst on the ACTIVE
  // sheet back to the value (or formula) it held before that burst. All
  // entries sharing the last batch id (a multi-cell paste) revert together.
  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    let last = -1;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].sheet === activeSheet) { last = i; break; }
    }
    if (last === -1) return;
    const batch = stack[last].batch;
    const entries = [];
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].sheet === activeSheet && stack[i].batch === batch) {
        entries.push(stack.splice(i, 1)[0]);
      }
    }
    const edits = entries.map((entry) => {
      let text;
      if (entry.prevFormula) {
        const f = String(entry.prevFormula);
        text = f.trim().startsWith("=") ? f : `=${f}`;
      } else {
        text = String(entry.prevValue == null ? "" : entry.prevValue);
      }
      if (!entry.formatChange) return { row: entry.row, col: entry.col, text };
      // Undo a formatted paste: restore the previous formatting too.
      return {
        row: entry.row, col: entry.col, text,
        style: entry.prevStyle,
        richHtml: entry.prevRich,
        dateFormat: entry.prevDateFormat,
        numFmt: entry.prevNumFmt,
        styleSource: entry.prevStyleSource || null,
      };
    });
    isUndoingRef.current = true;
    try {
      commitCells(edits);
    } finally {
      isUndoingRef.current = false;
    }
  }, [activeSheet, commitCells]);

  // Ctrl+Z / Cmd+Z while edit mode is on (outside text fields, which keep
  // their native input undo).
  useEffect(() => {
    if (!editMode || !canEdit) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
      if (String(e.key).toLowerCase() !== "z") return;
      const t = e.target;
      const tag = String(t.tagName || "").toLowerCase();
      // Text fields keep their native undo — EXCEPT the viewer's own
      // formula-bar input, which forwards Ctrl+Z here when it has no
      // pending draft (data-sev-undo-passthrough).
      if (
        (tag === "input" || tag === "textarea") &&
        !(t.dataset && t.dataset.sevUndoPassthrough === "true")
      ) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editMode, canEdit, undo]);

  // Record a sheet rename so it (a) survives per-sheet edit bookkeeping and
  // (b) is persisted into workbook.xml on save.
  const registerSheetRename = useCallback((oldName, newName) => {
    const ec = editedCellsRef.current;
    if (ec[oldName]) { ec[newName] = ec[oldName]; delete ec[oldName]; }
    const fc = formulaCellsRef.current;
    if (fc[oldName]) { fc[newName] = fc[oldName]; delete fc[oldName]; }
    undoStackRef.current.forEach((en) => { if (en.sheet === oldName) en.sheet = newName; });

    const originals = renameOriginalsRef.current;
    const orig = originals[oldName] || oldName;
    delete originals[oldName];
    if (orig !== newName) originals[newName] = orig;

    const anyEdits = Object.values(ec).some((s) => s && Object.keys(s).length > 0);
    setDirty(anyEdits || hasPendingRenames());
  }, [hasPendingRenames]);

  const save = useCallback(async () => {
    if (!canSave || saving || !dirty) return;
    if (!workbookBufferRef.current) {
      setStatusMsg("Original workbook unavailable");
      return;
    }
    try {
      setSaving(true);
      setStatusMsg("");

      // Patch ONLY the edited cells inside the original .xlsx, preserving
      // charts, drawings, images, styles and every other part byte-for-byte.
      // (ExcelJS was used here before, but it silently drops charts/drawings
      // when it re-writes the workbook.)
      // { originalName: currentName } for every pending rename.
      const sheetRenames = {};
      Object.entries(renameOriginalsRef.current).forEach(([current, original]) => {
        if (current !== original) sheetRenames[original] = current;
      });

      const outBytes = await patchWorkbookCells(
        workbookBufferRef.current.slice(0),
        editedCellsRef.current,
        sheetRenames,
        recalcRef.current
      );
      let bin = "";
      const CHUNK = 0x8000;
      for (let i = 0; i < outBytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(null, outBytes.subarray(i, i + CHUNK));
      }

      // Per-cell change list for the server-side edit-history log.
      const edits = [];
      Object.entries(editedCellsRef.current).forEach(([sheetName, cells]) => {
        Object.entries(cells).forEach(([key, entry]) => {
          const [r, c] = key.split(":").map(Number);
          edits.push({
            sheet: sheetName,
            row: r,
            col: c,
            cellRef: `${toColumnLabel(c)}${r + 1}`,
            oldValue: entry.oldValue,
            // Show the formula in history when the cell holds one.
            newValue: entry.formula
              ? `${entry.formula} = ${entry.value == null ? "" : entry.value}`
              : String(entry.value == null ? "" : entry.value),
          });
        });
      });

      const fileContentBase64 = btoa(bin);
      const url = `${process.env.REACT_APP_API}${config.__savePath}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          documentId,
          id: openId,
          fileName: fileName || "",
          fileExtension: "xlsx",
          fileContentBase64,
          edits,
        }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);

      // Future edits stack on the patched workbook (charts still intact).
      workbookBufferRef.current = outBytes.buffer.slice(
        outBytes.byteOffset,
        outBytes.byteOffset + outBytes.byteLength
      );
      editedCellsRef.current = {};
      recalcRef.current = {};
      sessionSnapshotRef.current = null;
      renameOriginalsRef.current = {};
      undoStackRef.current = [];
      // Hand the SAVED workbook to the page so re-opening this page (next /
      // previous navigation remounts the viewer) shows the saved data instead
      // of the originally downloaded file (QA: saved changes revert).
      if (typeof onWorkbookSavedRef.current === "function") {
        try { onWorkbookSavedRef.current(fileContentBase64); } catch (_) { /* non-fatal */ }
      }
      setDirty(false);
      setSaveSuccess(true);
      // Leave edit mode after a successful save so the toolbar shows the
      // Edit icon again (QA #8).
      setEditMode(false);
      setTimeout(() => setSaveSuccess(false), 6000);
    } catch (e) {
      console.error("[ExcelWebEdit] save error", e);
      setStatusMsg(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [canSave, saving, dirty, config, documentId, openId, fileName, workbookBufferRef]);

  // Fetch the full edit history for this document's spreadsheet (the
  // caller filters per cell). Available to any configured reader.
  const fetchHistory = useCallback(async () => {
    if (!config) return [];
    // Pass the current spreadsheet's fileName so multi-file documents return
    // the history for THIS file, not the first Excel input.
    const q = fileName ? `?fileName=${encodeURIComponent(fileName)}` : "";
    const url = `${process.env.REACT_APP_API}${config.__downloadPath}/${documentId}/${openId}/history${q}`;
    const res = await fetch(url, { method: "GET" });
    if (!res.ok) throw new Error(`History request failed (${res.status})`);
    const data = await res.json();
    return Array.isArray(data.entries) ? data.entries : [];
  }, [config, documentId, openId, fileName]);

  return {
    canEdit,
    canSave,
    editMode: editMode && canEdit,
    toggleEditMode,
    editBlocked,
    commitCell,
    commitCells,
    undo,
    registerSheetRename,
    save,
    dirty,
    saving,
    statusMsg,
    historyEnabled: !!config,
    fetchHistory,
    cancelEdits,
    saveSuccess,
    clearSaveSuccess: () => setSaveSuccess(false),
  };
}
